import * as yup from "yup";
import {
	CustomFieldDefinition,
	CustomFieldEntity,
	CustomFieldPlacement,
	CustomFieldValue,
	CustomFieldValues,
	JobFieldPlacement,
	ResolvedCustomFields,
} from "../../../models/Organisation";

/** The react-hook-form field group every custom-field control is registered under. */
export const CUSTOM_FIELDS_FORM_KEY = "customFields";

/**
 * What the form holds per field: strings for text/number/select/date inputs
 * (a number input's raw value is text until submit), booleans for checkboxes.
 * An inheritable override boolean is `undefined` while the job inherits.
 */
export type CustomFieldFormValues = Record<string, string | boolean | undefined>;

/** What `buildCustomFieldsPayload` accepts — form values, or a stored map passed straight through. */
type CustomFieldInputValues = Record<string, string | number | boolean | undefined>;

/**
 * The customer's values a job form inherits, keyed by field key — the
 * `source: "customer"` half of `Job.resolvedCustomFields`.
 */
export type InheritedCustomFields = Record<string, { value: CustomFieldValue; source: "customer" }>;

/** Segment values of the Ärv | Ja | Nej control for an inheritable override boolean. */
export const OVERRIDE_YES = "yes";
export const OVERRIDE_NO = "no";

// ---------------------------------------------------------------------------
// Placement, order and inheritance
// ---------------------------------------------------------------------------

/** Where a field renders when the definition carries no `placement`. */
export const DEFAULT_PLACEMENT: Record<CustomFieldEntity, CustomFieldPlacement> = {
	customer: "profil",
	job: "ovrigt",
	consultant: "ovrigt",
};

/** Pre-v2 customer placements, mapped defensively until the migration has run everywhere. */
const LEGACY_PLACEMENT: Record<string, CustomFieldPlacement> = {
	general: "profil",
	invoice: "faktura",
};

/** A definition's host card on its OWN entity's form (entity default when absent). */
export function customFieldPlacement(definition: CustomFieldDefinition): CustomFieldPlacement {
	const placement = definition.placement as string | undefined;
	if (!placement) return DEFAULT_PLACEMENT[definition.entity];
	return LEGACY_PLACEMENT[placement] ?? (placement as CustomFieldPlacement);
}

/**
 * A definition's host card on the JOB form: a job field's own placement, or
 * an inheritable customer field's `jobPlacement` ("ovrigt" when absent).
 */
export function jobCustomFieldPlacement(definition: CustomFieldDefinition): JobFieldPlacement {
	if (definition.entity === "customer") return definition.jobPlacement ?? "ovrigt";
	return customFieldPlacement(definition) as JobFieldPlacement;
}

/**
 * The definitions that render at `placement` on the `entity` form. On the job
 * form (`entity: "job"`) inherited customer definitions are placed by their
 * `jobPlacement`; everywhere else by the definition's own `placement`.
 */
export function filterCustomFieldsByPlacement(
	definitions: CustomFieldDefinition[],
	placement: CustomFieldPlacement,
	entity?: CustomFieldEntity
): CustomFieldDefinition[] {
	return definitions.filter((definition) =>
		entity === "job"
			? jobCustomFieldPlacement(definition) === placement
			: customFieldPlacement(definition) === placement
	);
}

/** The definitions placed on any of several host cards (same matching as `filterCustomFieldsByPlacement`). */
export function filterCustomFieldsByPlacements(
	definitions: CustomFieldDefinition[],
	placements: CustomFieldPlacement[],
	entity?: CustomFieldEntity
): CustomFieldDefinition[] {
	return definitions.filter((definition) =>
		placements.includes(
			entity === "job" ? jobCustomFieldPlacement(definition) : customFieldPlacement(definition)
		)
	);
}

/** Stable sort by `order` — definitions without one keep their insertion order after the ordered ones. */
export function sortCustomFieldDefinitions(
	definitions: CustomFieldDefinition[]
): CustomFieldDefinition[] {
	return definitions
		.map((definition, index) => ({ definition, index }))
		.sort((a, b) => {
			const orderA = a.definition.order ?? Number.POSITIVE_INFINITY;
			const orderB = b.definition.order ?? Number.POSITIVE_INFINITY;
			if (orderA !== orderB) return orderA < orderB ? -1 : 1;
			return a.index - b.index;
		})
		.map(({ definition }) => definition);
}

/** True for a customer field the job form shows (read-only) or may override. */
export function isInheritableCustomField(definition: CustomFieldDefinition): boolean {
	return (
		definition.entity === "customer" &&
		definition.inheritance !== undefined &&
		definition.inheritance !== "none"
	);
}

/** True for a customer field the job may set its own value for (tri-state on the job form). */
export function isOverridableCustomField(definition: CustomFieldDefinition): boolean {
	return definition.entity === "customer" && definition.inheritance === "override";
}

/**
 * True for a customer field the koncern owns: a member customer gets the
 * koncern's value read-only (non-members edit it like any other field).
 */
export function isGroupOwnedCustomField(definition: CustomFieldDefinition): boolean {
	return definition.entity === "customer" && definition.groupOwned === true;
}

/** The active customer definitions the koncern form edits, in display order (placement ignored). */
export function groupOwnedCustomFields(
	customerDefinitions: CustomFieldDefinition[]
): CustomFieldDefinition[] {
	return sortCustomFieldDefinitions(
		customerDefinitions.filter(
			(definition) => isGroupOwnedCustomField(definition) && !definition.archived
		)
	);
}

/** Options for the payload and schema helpers: which form they serve. */
export interface CustomerFieldOptions {
	/** The customer is a koncern member: group-owned keys are read-only — never sent, never required. */
	corporateGroupMember?: boolean;
	/**
	 * The form is a JOB form: customer fields with inheritance "show" are
	 * display-only there — never written on the job, never validated. On a
	 * customer form the same fields are the customer's own values and are
	 * sent and validated like any other.
	 */
	onJob?: boolean;
}

/** The customer definitions that take part in the job form, in their own order. */
export function inheritableCustomFields(
	customerDefinitions: CustomFieldDefinition[]
): CustomFieldDefinition[] {
	return customerDefinitions.filter(isInheritableCustomField);
}

/**
 * The full definition list a JOB form works with: the job's own fields plus
 * the inheritable customer fields. Payload, schema and seeding all run over
 * this list; `CustomFieldsSection` slices it per placement.
 */
export function jobFormCustomFieldDefinitions(
	jobDefinitions: CustomFieldDefinition[],
	customerDefinitions: CustomFieldDefinition[]
): CustomFieldDefinition[] {
	return [...jobDefinitions, ...inheritableCustomFields(customerDefinitions)];
}

// ---------------------------------------------------------------------------
// Inherited values (what the job form shows as "Ärvd från kund")
// ---------------------------------------------------------------------------

/**
 * The customer's values for its inheritable definitions — what a job of that
 * customer inherits. Used by the create page (selected customer) and as the
 * edit page's primary source (the customer is fetched there too, so the hint
 * shows the customer's value even when the job overrides it).
 */
export function inheritedCustomFieldsFromCustomer(
	customer: { customFields?: CustomFieldValues } | undefined,
	customerDefinitions: CustomFieldDefinition[]
): InheritedCustomFields {
	const inherited: InheritedCustomFields = {};
	if (!customer?.customFields) return inherited;
	customerDefinitions.forEach((definition) => {
		if (!isInheritableCustomField(definition)) return;
		const value = customer.customFields?.[definition.key];
		if (value !== undefined && value !== null) {
			inherited[definition.key] = { value, source: "customer" };
		}
	});
	return inherited;
}

/**
 * The inherited half of `GET /job/:jobId`'s `resolvedCustomFields` — the
 * entries the customer supplied. Entries the job overrides carry the job's
 * value (`source: "job"`), so they are left out here; prefer
 * {@link inheritedCustomFieldsFromCustomer} once the customer has loaded.
 */
export function inheritedCustomFieldsFromResolved(
	resolved: ResolvedCustomFields | undefined
): InheritedCustomFields {
	const inherited: InheritedCustomFields = {};
	Object.entries(resolved ?? {}).forEach(([key, entry]) => {
		if (entry.source === "customer") inherited[key] = { value: entry.value, source: "customer" };
	});
	return inherited;
}

/** Human-readable rendering of a stored value (option label for selects, Ja/Nej for booleans). */
export function formatCustomFieldValue(
	definition: CustomFieldDefinition,
	value: CustomFieldValue | undefined
): string {
	if (value === undefined || value === null || value === "") return "";
	switch (definition.type) {
		case "boolean":
			return value === true ? "Ja" : "Nej";
		case "select":
			return definition.options?.find((option) => option.key === value)?.label.sv ?? String(value);
		default:
			return String(value);
	}
}

// ---------------------------------------------------------------------------
// Form values
// ---------------------------------------------------------------------------

/**
 * Initial form values for a CREATE page: each definition's `defaultValue`
 * (booleans default to `false` so the checkbox is controlled from the start).
 * Inheritable override fields never take the default — on the job the
 * default is always "inherit" (`undefined` / `""`).
 */
export function customFieldDefaults(definitions: CustomFieldDefinition[]): CustomFieldFormValues {
	const values: CustomFieldFormValues = {};
	definitions.forEach((definition) => {
		values[definition.key] = isOverridableCustomField(definition)
			? toFormValue(definition, undefined)
			: toFormValue(definition, definition.defaultValue);
	});
	return values;
}

/**
 * Form values for an EDIT page seeded from the entity's stored map. Keys the
 * entity doesn't carry fall back to "unset" (never the default — a saved
 * entity without a value is "unset", not "default").
 */
export function customFieldFormValues(
	definitions: CustomFieldDefinition[],
	stored: CustomFieldValues | undefined
): CustomFieldFormValues {
	const values: CustomFieldFormValues = {};
	definitions.forEach((definition) => {
		values[definition.key] = toFormValue(definition, stored?.[definition.key]);
	});
	return values;
}

function toFormValue(
	definition: CustomFieldDefinition,
	value: CustomFieldValue | undefined
): string | boolean | undefined {
	if (definition.type === "boolean") {
		// Tri-state on the job: undefined = inherit, otherwise the explicit answer.
		if (isOverridableCustomField(definition))
			return value === undefined ? undefined : value === true;
		return value === true;
	}
	if (value === undefined || value === null) return "";
	return String(value);
}

/**
 * The Ja | Nej segment shown for an override boolean: the job's own value when
 * it has one, otherwise the customer's (preselected), otherwise Nej. The form
 * value stays `undefined` (= inherit) until the user picks a segment.
 */
export function overrideBooleanSegment(
	value: string | boolean | undefined,
	inheritedValue: string | number | boolean | undefined
): string {
	const shown = typeof value === "boolean" ? value : inheritedValue === true;
	return shown ? OVERRIDE_YES : OVERRIDE_NO;
}

/** The explicit form value a picked Ja | Nej segment maps to. */
export function overrideSegmentToValue(segment: string): boolean {
	return segment === OVERRIDE_YES;
}

// ---------------------------------------------------------------------------
// Payload
// ---------------------------------------------------------------------------

/**
 * The value map to send to the API: every ACTIVE key of the form's
 * definitions (the backend keeps archived values itself). Number `""` → key
 * omitted, otherwise `Number(value)`; empty strings are omitted; booleans are
 * sent whenever the checkbox was rendered (false included — an explicit "no"
 * is a value).
 *
 * Inheritable override fields (customer definitions on a job form) are the
 * exception: only keys the job explicitly sets are sent — an untouched
 * boolean (`undefined`) or an emptied input means "inherit" and is left out,
 * never sent as `false`/`""`. "show" fields are never written on a job
 * (`options.onJob`); on a customer form they are the customer's own values
 * and are sent like any other key.
 *
 * Customer forms of a koncern member (`options.corporateGroupMember`) leave
 * out every group-owned key — the backend would drop them anyway.
 *
 * Returns `undefined` when there are no definitions so entities without
 * custom fields keep an untouched payload.
 */
export function buildCustomFieldsPayload(
	values: CustomFieldInputValues | undefined,
	definitions: CustomFieldDefinition[],
	options: CustomerFieldOptions = {}
): CustomFieldValues | undefined {
	if (definitions.length === 0) return undefined;

	const payload: CustomFieldValues = {};
	definitions.forEach((definition) => {
		if (options.onJob && definition.entity === "customer" && definition.inheritance === "show")
			return;
		// A koncern member's group-owned value is the koncern's — never written on the customer.
		if (options.corporateGroupMember && isGroupOwnedCustomField(definition)) return;
		const raw = values?.[definition.key];
		switch (definition.type) {
			case "boolean":
				if (isOverridableCustomField(definition)) {
					if (typeof raw === "boolean") payload[definition.key] = raw;
					return;
				}
				payload[definition.key] = raw === true;
				break;
			case "number": {
				if (raw === undefined || raw === "" || typeof raw === "boolean") return;
				const num = typeof raw === "number" ? raw : Number(raw);
				if (Number.isFinite(num)) payload[definition.key] = num;
				break;
			}
			default: {
				if (typeof raw === "boolean" || raw === undefined) return;
				const text = String(raw).trim();
				if (text) payload[definition.key] = text;
			}
		}
	});
	return payload;
}

/**
 * Payload for a form that renders only a SUBSET of the entity's active
 * definitions (the customer Profil tab vs. the Fakturainformation tab): the
 * rendered keys come from the form, every other active key is read from the
 * entity's stored map, so a Profil save does not clear faktura values. The
 * backend merges archived keys itself, so those are not carried.
 */
export function buildCustomFieldsPayloadWithStored(
	values: CustomFieldInputValues | undefined,
	renderedDefinitions: CustomFieldDefinition[],
	allDefinitions: CustomFieldDefinition[],
	stored: CustomFieldValues | undefined,
	options: CustomerFieldOptions = {}
): CustomFieldValues | undefined {
	const renderedKeys = new Set(renderedDefinitions.map((definition) => definition.key));
	const unrendered = allDefinitions.filter((definition) => !renderedKeys.has(definition.key));
	return buildCustomFieldsPayload(
		{ ...customFieldFormValues(unrendered, stored), ...(values ?? {}) },
		allDefinitions,
		options
	);
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Yup shape for the `customFields` group: `required` definitions must have a
 * non-empty value (booleans are never required — unticked is a valid answer).
 * On a job form pass the `inherited` map: a required inheritable field is
 * valid when the customer supplies a value or the job overrides it. "show"
 * fields have no input on the job (`options.onJob`) and are not validated
 * there, and neither are a koncern member's group-owned fields
 * (`options.corporateGroupMember`). On a customer form a required "show"
 * field is validated like any other.
 * Append to a form's schema via `schema.shape({ customFields: customFieldsSchema(defs) })`.
 */
export function customFieldsSchema(
	definitions: CustomFieldDefinition[],
	inherited?: InheritedCustomFields,
	options: CustomerFieldOptions = {}
) {
	const shape: Record<string, yup.AnySchema> = {};
	definitions.forEach((definition) => {
		if (definition.type === "boolean") {
			shape[definition.key] = yup.boolean();
			return;
		}
		if (options.onJob && definition.entity === "customer" && definition.inheritance === "show")
			return;
		const base = yup.string();
		// A member's group-owned field is satisfied by the koncern's value.
		if (options.corporateGroupMember && isGroupOwnedCustomField(definition)) {
			shape[definition.key] = base.notRequired();
			return;
		}
		if (!definition.required) {
			shape[definition.key] = base.notRequired();
			return;
		}
		if (isOverridableCustomField(definition) && inherited?.[definition.key]?.value !== undefined) {
			shape[definition.key] = base.notRequired();
			return;
		}
		shape[definition.key] = base.required("FIELD_IS_REQUIRED");
	});
	return yup.object().shape(shape);
}
